import React, { useState } from 'react';
import './RegistrationPage.css';
import { getErrorMessage } from '../utils/api';

interface CoachRegistrationPageProps {
  onBackToLogin: () => void;
  onRegistrationSuccess: () => void;
}

const CoachRegistrationPage: React.FC<CoachRegistrationPageProps> = ({ onBackToLogin, onRegistrationSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    address: '',
    whatsapp_phone: '',
    licenses_certifications: '',
    sa_id_number: '',
    safa_number: '',
    date_of_birth: '',
    assigned_teams: [] as string[],
  });
  
  const availableTeams = [
    'Skilz School',
    'U12 A',
    'U12 B',
    'U14 A',
    'U14 B',
    'U16 A',
    'U16 B',
    'U18',
    'Seniors'
  ];
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    if (name === 'last_name') {
      setFormData({ ...formData, [name]: value.toUpperCase() });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleTeamChange = (team: string) => {
    setFormData(prev => {
      const teams = prev.assigned_teams;
      if (teams.includes(team)) {
        return { ...prev, assigned_teams: teams.filter(t => t !== team) };
      } else {
        return { ...prev, assigned_teams: [...teams, team] };
      }
    });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Required fields based on backend validation
    if (!formData.username.trim()) errors.username = 'Username is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.password) errors.password = 'Password is required';
    if (!formData.first_name.trim()) errors.first_name = 'First name is required';
    if (!formData.last_name.trim()) errors.last_name = 'Last name is required';
    if (!formData.address.trim()) errors.address = 'Address is required';
    if (!formData.whatsapp_phone.trim()) errors.whatsapp_phone = 'WhatsApp phone is required';
    
    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Password validation
    if (formData.password) {
      if (formData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters long';
      } else if (!/[A-Z]/.test(formData.password)) {
        errors.password = 'Password must contain at least 1 capital letter';
      } else if (!/[0-9]/.test(formData.password)) {
        errors.password = 'Password must contain at least 1 number';
      }
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    
    // Validate form before submission
    if (!validateForm()) {
      setError('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {

      const { buildApiUrl } = require('../config/api');
      const response = await fetch(buildApiUrl('/auth/register/coach'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name.toUpperCase(),
          address: formData.address,
          whatsapp_phone: formData.whatsapp_phone,
          teams: formData.assigned_teams.length > 0 ? JSON.stringify(formData.assigned_teams) : null,
          licenses_certifications: formData.licenses_certifications || null,
          sa_id_number: formData.sa_id_number || null,
          safa_number: formData.safa_number || null,
          date_of_birth: formData.date_of_birth || null,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        alert(`Registration successful! Your credentials have been sent to your WhatsApp number: ${formData.whatsapp_phone}\n\nUsername: ${data.username}`);
        onRegistrationSuccess();
      } else {
        setError(getErrorMessage(data, 'Registration failed'));
      }
    } catch (err) {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="registration-page">
      <div className="registration-container">
        <h2>Coach Registration</h2>
        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
          {/* Hidden dummy fields to prevent browser autocomplete */}
          <input type="text" style={{ position: 'absolute', left: '-9999px', opacity: 0 }} autoComplete="off" tabIndex={-1} readOnly />
          <input type="password" style={{ position: 'absolute', left: '-9999px', opacity: 0 }} autoComplete="off" tabIndex={-1} readOnly />
          <input type="email" style={{ position: 'absolute', left: '-9999px', opacity: 0 }} autoComplete="off" tabIndex={-1} readOnly />
          <div className="form-row">
            <div className="form-group">
              <label>Username *</label>
              <input 
                type="text" 
                name="username" 
                value={formData.username} 
                onChange={handleChange} 
                required 
                autoComplete="off" 
                id="coach-username"
                className={fieldErrors.username ? 'field-error' : ''}
              />
              {fieldErrors.username && <span className="field-error-message">{fieldErrors.username}</span>}
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input 
                type="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange} 
                required 
                autoComplete="off"
                className={fieldErrors.email ? 'field-error' : ''}
              />
              {fieldErrors.email && <span className="field-error-message">{fieldErrors.email}</span>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input 
                type="text" 
                name="first_name" 
                value={formData.first_name} 
                onChange={handleChange} 
                required 
                autoComplete="off"
                className={fieldErrors.first_name ? 'field-error' : ''}
              />
              {fieldErrors.first_name && <span className="field-error-message">{fieldErrors.first_name}</span>}
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input 
                type="text" 
                name="last_name" 
                value={formData.last_name} 
                onChange={handleChange} 
                required 
                autoComplete="off"
                className={fieldErrors.last_name ? 'field-error' : ''}
              />
              {fieldErrors.last_name && <span className="field-error-message">{fieldErrors.last_name}</span>}
            </div>
          </div>
          <div className="form-group">
            <label>Password *</label>
            <input 
              type="password" 
              name="password" 
              value={formData.password} 
              onChange={handleChange} 
              required 
              autoComplete="new-password" 
              id="coach-password"
              data-lpignore="true"
              data-form-type="other"
              className={fieldErrors.password ? 'field-error' : ''}
            />
            {fieldErrors.password && <span className="field-error-message">{fieldErrors.password}</span>}
          </div>
          <div className="form-group">
            <label>Address *</label>
            <input 
              type="text" 
              name="address" 
              value={formData.address} 
              onChange={handleChange} 
              required 
              autoComplete="off"
              className={fieldErrors.address ? 'field-error' : ''}
            />
            {fieldErrors.address && <span className="field-error-message">{fieldErrors.address}</span>}
          </div>
          <div className="form-group">
            <label>WhatsApp Phone *</label>
            <input 
              type="tel" 
              name="whatsapp_phone" 
              value={formData.whatsapp_phone} 
              onChange={handleChange} 
              required 
              autoComplete="off"
              className={fieldErrors.whatsapp_phone ? 'field-error' : ''}
            />
            {fieldErrors.whatsapp_phone && <span className="field-error-message">{fieldErrors.whatsapp_phone}</span>}
          </div>
          <div className="form-group">
            <label>Assigned Teams *</label>
            <div className="teams-multiselect">
              {availableTeams.map(team => (
                <label key={team} className="team-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.assigned_teams.includes(team)}
                    onChange={() => handleTeamChange(team)}
                  />
                  <span>{team}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Date of Birth</label>
            <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} autoComplete="off" />
          </div>
          <div className="form-group">
            <label>Licenses/Certifications</label>
            <textarea name="licenses_certifications" value={formData.licenses_certifications} onChange={handleChange} autoComplete="off" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>SA ID Number</label>
              <input type="text" name="sa_id_number" value={formData.sa_id_number} onChange={handleChange} autoComplete="off" />
            </div>
            <div className="form-group">
              <label>SAFA Number</label>
              <input type="text" name="safa_number" value={formData.safa_number} onChange={handleChange} autoComplete="off" />
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>
        <button onClick={onBackToLogin} className="back-btn">Back to Login</button>
      </div>
    </div>
  );
};

export default CoachRegistrationPage;


